import React, { useState } from "react";
import PropTypes from "prop-types";

import clientApiController from "../../../services/client.service";
import {
  appointmentContact,
  normalizeClientRelationship,
} from "./clientDetailUx";


const responsePayload = (response) => response && response.data !== undefined ? response.data : response;

const clientRows = (response) => {
  const payload = responsePayload(response);
  if (Array.isArray(payload)) return payload;
  return payload && Array.isArray(payload.data) ? payload.data : [];
};

const errorMessage = (error) => (
  error?.response?.data?.error?.message
  || error?.response?.data?.message
  || error?.message
  || "The linked profile could not be updated."
);

const clientName = (client) => `${client?.first_name || ""} ${client?.last_name || ""}`.trim();

const ParentChildProfiles = ({
  detail,
  api = clientApiController,
  canCreate = false,
  canUpdate = false,
  onChanged = () => {},
}) => {
  const relationship = normalizeClientRelationship(detail);
  const contact = appointmentContact(detail);
  const [mode, setMode] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [parentId, setParentId] = useState("");
  const [child, setChild] = useState({
    first_name: "",
    last_name: "",
    date_of_birth: "",
    gender: "",
    send_email_notification: true,
    send_sms_notification: true,
  });

  const finish = async (request) => {
    setSaving(true);
    setError("");
    try {
      const response = await request;
      setMode("");
      onChanged(responsePayload(response));
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  };

  const openParentPicker = async () => {
    setMode("link");
    setError("");
    try {
      const response = await api.parentCandidates({ child_client_id: Number(detail.id) });
      setCandidates(clientRows(response));
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const saveChild = (event) => {
    event.preventDefault();
    if (!child.first_name.trim() || !child.last_name.trim()) {
      setError("Child first and last name are required.");
      return;
    }
    finish(api.childStore({ ...child, parent_client_id: Number(detail.id) }));
  };

  return (
    <section className="card mb-3" aria-label="Linked profiles">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h3 className="h6 mb-0">Parent and child profiles</h3>
          {canCreate && relationship.role !== "child" && (
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setMode("child")}>Add child</button>
          )}
        </div>

        {relationship.parent && (
          <div className="mb-2">
            <strong>Parent:</strong> <span>{clientName(relationship.parent)}</span>
            {canUpdate && <button type="button" className="btn btn-link btn-sm" disabled={saving} onClick={() => finish(api.parentUnlink({ child_client_id: Number(detail.id) }))}>Unlink parent</button>}
          </div>
        )}
        {relationship.children.length > 0 && (
          <div className="mb-2">
            <strong>Children:</strong>
            <ul className="mb-0">{relationship.children.map((linked) => <li key={linked.id}>{clientName(linked)}</li>)}</ul>
          </div>
        )}
        {(contact.email || contact.phone_number) && (
          <p className="small mb-2">
            Appointment contact: {[contact.email, contact.phone_number].filter(Boolean).join(" · ")}
            {(contact.email_inherited || contact.phone_inherited) ? " (inherited from parent)" : ""}
          </p>
        )}
        {canUpdate && relationship.role === null && mode !== "link" && (
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={openParentPicker}>Link to parent</button>
        )}

        {mode === "link" && (
          <div className="mt-2">
            <label htmlFor="parent-client" className="form-label">Parent client</label>
            <select id="parent-client" className="form-control" value={parentId} onChange={(event) => setParentId(event.target.value)}>
              <option value="">Select a parent</option>
              {candidates.map((candidate) => <option key={candidate.id} value={candidate.id}>{clientName(candidate)}</option>)}
            </select>
            {candidates.length === 0 && <p className="small text-muted mt-2 mb-0">No eligible parents available for this client.</p>}
            <button type="button" className="btn btn-primary btn-sm mt-2" disabled={saving || !parentId} onClick={() => finish(api.parentLink({ parent_client_id: Number(parentId), child_client_id: Number(detail.id) }))}>Link parent</button>
            <button type="button" className="btn btn-link btn-sm mt-2" onClick={() => setMode("")}>Cancel</button>
          </div>
        )}

        {mode === "child" && (
          <form className="mt-3" onSubmit={saveChild}>
            <label htmlFor="child-first-name" className="form-label">Child first name</label>
            <input id="child-first-name" className="form-control mb-2" value={child.first_name} onChange={(event) => setChild({ ...child, first_name: event.target.value })} />
            <label htmlFor="child-last-name" className="form-label">Child last name</label>
            <input id="child-last-name" className="form-control mb-2" value={child.last_name} onChange={(event) => setChild({ ...child, last_name: event.target.value })} />
            <label htmlFor="child-date-of-birth" className="form-label">Date of birth (optional)</label>
            <input id="child-date-of-birth" type="date" className="form-control mb-2" value={child.date_of_birth} onChange={(event) => setChild({ ...child, date_of_birth: event.target.value })} />
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>Save child</button>
            <button type="button" className="btn btn-link btn-sm" onClick={() => setMode("")}>Cancel</button>
          </form>
        )}
        {error && <div className="alert alert-danger mt-2 mb-0" role="alert">{error}</div>}
      </div>
    </section>
  );
};

ParentChildProfiles.propTypes = {
  detail: PropTypes.object.isRequired,
  api: PropTypes.shape({
    childStore: PropTypes.func,
    parentLink: PropTypes.func,
    parentCandidates: PropTypes.func,
    parentUnlink: PropTypes.func,
  }),
  canCreate: PropTypes.bool,
  canUpdate: PropTypes.bool,
  onChanged: PropTypes.func,
};

export default ParentChildProfiles;
